import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Pencil, GripVertical, Tag, AlertCircle, Save, X, GitMerge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const emptyForm = { name: "", description: "", sort_order: 0 };

export default function ProductCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);
  const [toMerge, setToMerge] = useState(null);           // Phase 101ea.5 — source à fusionner
  const [mergeTargetId, setMergeTargetId] = useState(""); // Cible sélectionnée
  const [mergeSaving, setMergeSaving] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/product-categories");
    // Récupère les product_count via l'endpoint public shop
    try {
      const { data: shopCats } = await api.get("/shop/categories");
      const countsBySlug = Object.fromEntries(shopCats.map((c) => [c.id, c.product_count]));
      setItems(data.map((c) => ({ ...c, product_count: countsBySlug[c.id] ?? 0 })));
    } catch {
      setItems(data);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description || "", sort_order: c.sort_order || 0 });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/product-categories/${editing.id}`, form);
        toast.success("Catégorie mise à jour");
      } else {
        await api.post("/product-categories", form);
        toast.success("Catégorie créée");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/product-categories/${c.id}`);
      toast.success(`Catégorie "${c.name}" supprimée`);
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  // Phase 101ea.5 — Fusion catégorie source → cible
  const openMerge = (c) => {
    setToMerge(c);
    setMergeTargetId("");
  };
  const doMerge = async () => {
    if (!toMerge || !mergeTargetId) {
      toast.error("Choisis une catégorie cible");
      return;
    }
    setMergeSaving(true);
    try {
      const { data } = await api.post(`/product-categories/${toMerge.id}/merge-into`, {
        target_category_id: mergeTargetId,
      });
      toast.success(
        `« ${data.source_name} » fusionnée dans « ${data.target_name} » — ${data.products_moved} produit(s) déplacé(s)`
      );
      setToMerge(null);
      setMergeTargetId("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fusion impossible");
    } finally {
      setMergeSaving(false);
    }
  };

  const mergeTargets = useMemo(
    () => items.filter((x) => toMerge && x.id !== toMerge.id),
    [items, toMerge]
  );

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <p className="text-sm text-[#575A57]">
            Gérez les catégories visibles sur la boutique en ligne. Réorganisez l'ordre d'affichage avec le champ <em>ordre</em>.
            Pour éliminer un doublon avec des produits déjà rattachés, utilisez <b>« Fusionner »</b> (icône <GitMerge className="w-3 h-3 inline text-[#8C6B2F]" />) plutôt que Supprimer — tous les produits basculent vers la catégorie cible sans être perdus.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-category-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle catégorie
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editing ? "Modifier la catégorie" : "Nouvelle catégorie"}
              </DialogTitle>
              <DialogDescription>
                {editing
                  ? "Le renommage propage automatiquement le nouveau nom à tous les produits liés."
                  : "Le slug URL-friendly est généré automatiquement à partir du nom."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block">Nom *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Poulet, Crevettes, Plats végétariens…"
                  data-testid="cat-name"
                  maxLength={60}
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Description (optionnelle, vitrine)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Présentation courte affichable en boutique"
                  data-testid="cat-description"
                  maxLength={300}
                />
                <div className="text-[10px] text-[#A3A6A3] mt-1 text-right font-mono">
                  {(form.description || "").length}/300
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0", 10) })}
                  className="w-24 font-mono"
                  data-testid="cat-order"
                />
                <p className="text-[11px] text-[#A3A6A3] mt-1">
                  Plus petit = affiché en premier. Égalité → tri alphabétique.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button onClick={save} disabled={saving} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-category-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement…" : (editing ? "Mettre à jour" : "Créer")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] w-16 text-center">
                <GripVertical className="w-4 h-4 inline" />
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Slug</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Description</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-center">Produits</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="categories-tbody">
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#575A57] py-12">
                  <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Aucune catégorie. Créez la première !
                </TableCell>
              </TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                <TableCell className="text-center font-mono text-xs text-[#A3A6A3]">{c.sort_order}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-xs text-[#575A57]">{c.slug}</TableCell>
                <TableCell className="text-sm text-[#575A57] max-w-md truncate">{c.description || <span className="text-[#A3A6A3] italic">—</span>}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={c.product_count > 0 ? "bg-[#EAF3EE] border-[#B4D3A7] text-[#1F4E3D]" : "bg-[#F4F4F0] border-[#E5E5E0] text-[#A3A6A3]"}>
                    {c.product_count ?? 0}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openMerge(c)}
                      title="Fusionner dans une autre catégorie"
                      data-testid={`merge-category-${c.id}`}
                    >
                      <GitMerge className="w-4 h-4 text-[#8C6B2F]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setToDelete(c)} data-testid={`delete-category-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-[#C05C47]" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Phase 101ea.5 — Dialog Fusionner catégorie */}
      <Dialog open={!!toMerge} onOpenChange={(o) => { if (!o) { setToMerge(null); setMergeTargetId(""); } }}>
        <DialogContent className="max-w-lg" data-testid="merge-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-[#8C6B2F]" />
              Fusionner « {toMerge?.name} »
            </DialogTitle>
            <DialogDescription>
              Tous les produits associés à <b>« {toMerge?.name} »</b>
              {toMerge?.product_count > 0 && (
                <> (<b>{toMerge.product_count}</b> produit{toMerge.product_count > 1 ? "s" : ""})</>
              )}
              {" "}seront déplacés vers la catégorie cible, puis la catégorie source sera supprimée.
              Cette action est irréversible mais sûre — aucun produit n'est perdu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Catégorie cible *</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger data-testid="merge-target-select">
                  <SelectValue placeholder="Choisis la catégorie qui restera…" />
                </SelectTrigger>
                <SelectContent>
                  {mergeTargets.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[#575A57] italic">
                      Aucune autre catégorie disponible
                    </div>
                  ) : mergeTargets.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      <span className="text-[#A3A6A3] text-xs ml-2">
                        ({t.product_count ?? 0} produit{(t.product_count ?? 0) > 1 ? "s" : ""})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {mergeTargetId && (
              <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-3 text-sm">
                <div className="text-[#2F5A8C]">
                  <b>{toMerge?.product_count ?? 0}</b> produit{(toMerge?.product_count ?? 0) > 1 ? "s" : ""} de
                  {" "}<b>« {toMerge?.name} »</b> → basculé{(toMerge?.product_count ?? 0) > 1 ? "s" : ""} dans{" "}
                  <b>« {items.find((x) => x.id === mergeTargetId)?.name} »</b>
                </div>
                <div className="text-[10px] text-[#575A57] mt-1">
                  Puis la catégorie « {toMerge?.name} » sera supprimée.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setToMerge(null); setMergeTargetId(""); }} data-testid="cancel-merge-btn">
              Annuler
            </Button>
            <Button
              onClick={doMerge}
              disabled={mergeSaving || !mergeTargetId}
              className="bg-[#8C6B2F] hover:bg-[#725520] text-white"
              data-testid="confirm-merge-btn"
            >
              {mergeSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <GitMerge className="w-4 h-4 mr-1" />}
              Fusionner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#C05C47]" />
              Supprimer la catégorie ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.name}</strong> sera supprimée. Si des produits l'utilisent encore, la suppression sera refusée — vous devrez les réaffecter avant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cat-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toDelete && remove(toDelete)}
              className="bg-[#C05C47] hover:bg-[#A64A37]"
              data-testid="confirm-delete-cat-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
